// PatentBoardEdit.tsx
import { observer } from "mobx-react-lite";
import MainLayout from "../MainLayout";
import { useLocation, useNavigate } from "react-router-dom";
import { VerticalLayout } from "../../common/common-layout";
import { TitleLargeMedium, TitleMediumBold } from "../../common/common-text";
import { useRef, useState } from "react";
import BTBox from "../../common/components/BTBox";
import { BTSelectBox } from "../../common/components/BTSelectBox";
import { BTDateComponent } from "../../common/components/BTDateComponent";
import { BTCheckBox } from "../../common/components/BTCheckBox";
import { authStore } from "../../store/AuthStore";
import { baseStore } from "../../store/BaseStore";
import { dialogStore } from "../../store/DialogStore";
import { requestDeleteFile, requestUpdatePatentDocuments, requestUploadFiles } from "../../service/BoardService";
import TextField from "@mui/material/TextField";
import BTButton from "../../common/components/BTButton";
import Divider from "@mui/material/Divider";
import theme from "../../common/theme";
import FileIcon from "../../assets/icons/file_icon.svg";

const INPUT_H = 36;      // Date 컴포넌트와 동일 높이

const PatentBoardEdit = () => (
  <MainLayout>
    <PatentBoardEditBody />
  </MainLayout>
);

export default PatentBoardEdit;

const PatentBoardEditBody = observer(() => {
  const location = useLocation();
  const navigate = useNavigate();

  const statePatent = location.state?.patent;
  const patentTypeOptions = location.state?.patentTypeOptions || [];

  const [title, setTitle] = useState(statePatent?.title || "");
  const [englishTitle, setEnglishTitle] = useState(statePatent?.englishTitle || "");
  const [chineseTitle, setChineseTitle] = useState(statePatent?.chineseTitle || "");
  const [representImageUrl, setRepresentImageUrl] = useState<string | null>(statePatent?.representImage || null);
  const [representImageFile, setRepresentImageFile] = useState<File | null>(null);
  const [patentType, setPatentType] = useState(statePatent?.patentType || "");
  const [acquisitionDate, setAcquisitionDate] = useState(statePatent?.acquisitionDate || "");
  const [isPublished, setIsPublished] = useState(statePatent?.isPublished ?? true);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) {
      setRepresentImageFile(files[0]);
      setRepresentImageUrl(URL.createObjectURL(files[0])); // 즉시 미리보기
    }
  };

  const handleSave = async () => {
    try {
      baseStore.showLoading();

      // 새 파일이 있다면: 기존 파일 삭제 → 새 파일 업로드
      if (representImageFile) {
        try {
          if (statePatent.representImage) {
            const match = statePatent.representImage.match(/\/o\/([^?]+)\?/);
            if (match) {
              const path = decodeURIComponent(match[1]);
              await requestDeleteFile(path);
            }
          }
        } catch {
          // 삭제 실패는 무시
        }

        const [uploadedUrl] = await requestUploadFiles("patent", statePatent.id, "attachments", [representImageFile]);
        statePatent.representImage = uploadedUrl;
      }

      // 폼 값 반영
      statePatent.patentType = patentType;
      statePatent.title = title;
      statePatent.englishTitle = englishTitle;
      statePatent.chineseTitle = chineseTitle;
      statePatent.acquisitionDate = acquisitionDate ? new Date(acquisitionDate) : null;
      statePatent.isPublished = isPublished;
      statePatent.updatedBy = authStore.user?.username ?? "";
      statePatent.updatedAt = new Date();

      await requestUpdatePatentDocuments(statePatent);

      dialogStore.showDialog({
        title: "성공",
        message: "특허건이 성공적으로 수정되었습니다.",
        onConfirm: () => navigate("/board/patent/detail", { state: { patent: statePatent, patentTypeOptions } }),
        onClose: () => navigate("/board/patent/detail", { state: { patent: statePatent, patentTypeOptions } }),
      });
    } catch (e) {
      dialogStore.showDialog({
        title: "오류",
        message: "특허건 수정 중 문제가 발생했습니다. 다시 시도해 주세요.",
        onConfirm: () => { },
      });
    } finally {
      baseStore.hideLoading();
    }
  };

  return (
    <VerticalLayout height="auto" paddingLeft={36} paddingRight={36}>
      <TitleLargeMedium>특허 수정</TitleLargeMedium>
      <BTBox height="24px" />
      <Divider style={{ width: "100%", backgroundColor: '#D9D9D9' }} />

      <VerticalLayout height={"auto"} paddingBottom={36}>
        {/* 섹션: 특허 정보 */}
        <TitleMediumBold style={{ marginTop: "20px" }}>특허 정보</TitleMediumBold>
        <BTBox height="16px" />

        {/* 1줄: 특허 유형 */}
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "nowrap", fontSize: "14px", color: "#6C757D" }}>
          <b style={{ whiteSpace: "nowrap", width: '60px' }}>특허 유형</b>
          <div style={{ width: 260, flex: "0 0 auto" }}>
            <BTSelectBox
              width="100%"
              options={patentTypeOptions}
              value={patentType}
              onChange={(e) => setPatentType(e.target.value)}
            />
          </div>
        </div>

        <BTBox height="16px" />

        {/* 2줄: 특허명 → Divider → 취득일자 → Divider → 공개 여부 */}
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "nowrap", fontSize: "14px", color: "#6C757D" }}>
          {/* 특허명 */}
          <b style={{ whiteSpace: "nowrap", width: '60px' }}>특허명</b>
          <div style={{ width: 260, flex: "0 0 auto" }}>
            <TextField
              label=""
              variant="outlined"
              fullWidth
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              size="small"
              sx={{
                '& .MuiInputBase-root': { height: `${INPUT_H}px` },
                '& .MuiOutlinedInput-input': {
                  padding: '0 12px',
                  height: `${INPUT_H}px`,
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
            />
          </div>

          <Divider orientation="vertical" sx={{ borderColor: "#6C757D", mx: "4px", height: "16px" }} />

          {/* 특허 취득 일자 */}
          <b style={{ whiteSpace: "nowrap", width: '80px' }}>특허 취득 일자</b>
          <div style={{ width: 220, flex: "0 0 auto" }}>
            <BTDateComponent width="100%" value={acquisitionDate} onChange={setAcquisitionDate} />
          </div>

          <Divider orientation="vertical" sx={{ borderColor: "#6C757D", mx: "4px", height: "16px" }} />

          {/* 공개 여부 */}
          <b style={{ whiteSpace: "nowrap", flex: "0 0 auto", flexShrink: 0 }}>글 공개 여부</b>
          <BTCheckBox title="" checked={isPublished} onChange={(e) => setIsPublished(e.target.checked)} />
        </div>

        <BTBox height="16px" />

        {/* 1.5줄: 영문/중문 특허명 */}
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "nowrap", fontSize: "14px", color: "#6C757D" }}>
          {/* 영문 특허명 */}
          <b style={{ whiteSpace: "nowrap", width: '60px' }}>영문 특허명</b>
          <div style={{ width: 260, flex: "0 0 auto" }}>
            <TextField
              label=""
              variant="outlined"
              fullWidth
              value={englishTitle}
              onChange={(e) => setEnglishTitle(e.target.value)}
              size="small"
              sx={{
                '& .MuiInputBase-root': { height: `${INPUT_H}px` },
                '& .MuiOutlinedInput-input': {
                  padding: '0 12px',
                  height: `${INPUT_H}px`,
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
            />
          </div>

          <Divider orientation="vertical" sx={{ borderColor: "#6C757D", mx: "4px", height: "16px" }} />

          {/* 중문 특허명 */}
          <b style={{ whiteSpace: "nowrap", width: '80px' }}>중문 특허명</b>
          <div style={{ width: 260, flex: "0 0 auto" }}>
            <TextField
              label=""
              variant="outlined"
              fullWidth
              value={chineseTitle}
              onChange={(e) => setChineseTitle(e.target.value)}
              size="small"
              sx={{
                '& .MuiInputBase-root': { height: `${INPUT_H}px` },
                '& .MuiOutlinedInput-input': {
                  padding: '0 12px',
                  height: `${INPUT_H}px`,
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
            />
          </div>
        </div>

        <BTBox height="16px" />
        <Divider style={{ width: "100%", backgroundColor: '#D9D9D9' }} />

        {/* 섹션: 대표 이미지 */}
        <TitleMediumBold style={{ marginTop: "20px" }}>특허 이미지</TitleMediumBold>
        <BTBox height="16px" />

        {/* ✅ 항상 표시되는 프리뷰/플레이스홀더 박스 */}
        <div
          role="button"
          tabIndex={0}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
          }}
          aria-label="대표 이미지 선택"
          style={{
            width: 200,
            height: 300,
            border: representImageUrl ? "1px solid #ccc" : "2px dashed #C9CDD2",
            borderRadius: 6,
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: representImageUrl ? "#fafafa" : "#FBFBFC",
            cursor: "pointer",
            userSelect: "none",
            transition: "border-color 0.15s ease",
          }}
        >
          {representImageUrl ? (
            <img
              src={representImageUrl}
              alt="첨부된 대표 이미지 미리보기"
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "cover" }}
            />
          ) : (
            <div style={{ textAlign: "center", color: "#8A92A6", lineHeight: 1.4 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>이미지를 첨부해주세요</div>
              <div style={{ fontSize: 12, marginTop: 6 }}>
                아래 <b>파일 선택</b> 버튼 또는 이 영역을 클릭
              </div>
              <div style={{ fontSize: 11, marginTop: 4, color: "#ADB5BD" }}>
                (권장: JPG/PNG, 5MB 이하)
              </div>
            </div>
          )}
        </div>

        <BTBox height="16px" />

        {/* 파일 선택 버튼 (숨긴 input + BTButton) — 기존과 동일 */}
        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 12 }}>
          <input
            ref={fileInputRef}
            id="patent-edit-file-input"
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={{
              position: "absolute",
              width: 1,
              height: 1,
              padding: 0,
              margin: -1,
              overflow: "hidden",
              clip: "rect(0 0 0 0)",
              whiteSpace: "nowrap",
              border: 0,
            }}
          />
          <BTButton
            width="auto"
            height="40px"
            color="#E7E7E7"
            textColor="#4E6173"
            onClick={() => fileInputRef.current?.click()}
            sx={{ px: 2, display: "inline-flex", alignItems: "center", gap: 1, fontSize: "14px" }}
            aria-label="파일 선택"
          >
            <img src={FileIcon} />
            <span>파일 선택</span>
          </BTButton>
        </div>

        <BTBox height="36px" />

        {/* 액션 버튼 */}
        <div style={{ width: "100%", display: "flex", justifyContent: "flex-end", gap: "16px" }}>
          <BTButton color={theme.colors.bbTech} textColor="white" onClick={handleSave}>저장</BTButton>
          <BTButton onClick={() => navigate(-1)}>취소</BTButton>
        </div>
      </VerticalLayout>
    </VerticalLayout>
  );
});